import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Container,
  Typography,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  SxProps,
} from "@mui/material";
import { client } from "../api/client";
import { baseUrl, colors } from "../utils/constants";
import { REGISTER } from "../api/endpoints";
import { getAllLiteRanks } from "../api/requests/ranks";
import { useAlert } from "../context/AlertContext";
import { getLiteRoles } from "../api/requests/roles";
import dayjs from "dayjs";
import { getAllZone } from "../api/requests/zones";
import { AssemblyConstituencyType, RIOType, ZoneType } from "../utils/types";
import { getAllAssemblyConstituency } from "../api/requests/assemblyConstituency";
import { getAllRIO } from "../api/requests/RIO";

type Rank = {
  id: number;
  rankName: string;
  count: number;
};

type Role = {
  roleId: number;
  roleName: string;
  description: string;
};

interface FormData {
  fullName: string;
  mobileNo: string;
  email: string;
  rank: number | null;
  policeStation: string;
  zoneId: number | null;
  location: string;
  dateOfBirth: string;
  role: number | null;
  userId: number | null;
  assemblyConstituencyId: number | null;
  rioId: number | null;
  employeeId: number | null;
  generalNumber: number | null;
}

interface UserFormProps {
  mode: string;
  closeUserDialog: (buttonText: string) => void;
  userId: number | null;
}

const UserForm: React.FC<UserFormProps> = ({
  mode,
  closeUserDialog,
  userId,
}) => {
  const { showAlert } = useAlert();
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [allZones, setAllZones] = useState<ZoneType[]>([]);
  const [filteredZones, setFilteredZones] = useState<ZoneType[]>([]);
  const [filteredAssemblyConstituency, setFilteredAssemblyConstituency] =
    useState<AssemblyConstituencyType[]>([]);

  const [assemblyConstituency, setAssemblyConstituency] = useState<
    AssemblyConstituencyType[]
  >([]);
  const [rios, setRIOS] = useState<RIOType[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);

  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    mobileNo: "",
    email: "",
    rank: null,
    policeStation: "",
    zoneId: null,
    location: "",
    dateOfBirth: "",
    role: null,
    userId: null,
    assemblyConstituencyId: null,
    rioId: null,
    generalNumber: null,
    employeeId: null,
  });

  const filterZonesByRIO = (rioId: number | null) => {
    if (!rioId) {
      setFilteredZones([]);
      return;
    }
    const filtered = allZones.filter((zone) => zone.rioId === rioId);
    setFilteredZones(filtered);

    if (
      formData.zoneId &&
      !filtered.some((zone) => zone.zoneId === formData.zoneId)
    ) {
      setFormData((prev) => ({ ...prev, zoneId: null }));
    }
  };

  const filterAssemblyByZone = (zoneId: number | null) => {
    if (!zoneId) {
      setAssemblyConstituency([]);
      return;
    }
    const filtered = assemblyConstituency.filter((ac) => ac.zoneId === zoneId);
    setFilteredAssemblyConstituency(filtered);

    if (
      formData.assemblyConstituencyId &&
      !filtered.some(
        (ac) => ac.assemblyConstituencyId === formData.assemblyConstituencyId
      )
    ) {
      setFormData((prev) => ({ ...prev, assemblyConstituencyId: null }));
    }
  };

  const fetchAllRanks = async () => {
    try {
      const response = await getAllLiteRanks();
      if (response.status === 200) {
        setRanks(
          response.data.map((rank: any) => ({
            id: rank.rankId,
            rankName: rank.rankName,
            count: rank.usersCount,
          }))
        );
      } else {
        showAlert("Failed to fetch ranks.", "error");
      }
    } catch {
      showAlert("An error occurred while fetching ranks.", "error");
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await getLiteRoles();
      if (response && response.data) {
        const mappedRoles = response.data.map((item: any) => ({
          roleId: Number(item.roleId),
          roleName: item.roleName,
          description: item.description,
        }));
        setRoles(mappedRoles);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      showAlert("An error occurred while fetching roles.", "error");
    }
  };

  const fetchZones = async () => {
    try {
      const response = await getAllZone();
      if (response && response.data) {
        const mappedZones = response.data.map((item: ZoneType) => ({
          id: item.zoneId,
          ...item,
        }));
        setAllZones(mappedZones);
      }
    } catch (error) {
      console.error("Error fetching zones:", error);
      showAlert("An error occurred while fetching zones.", "error");
    }
  };
  const fetchRIOs = async () => {
    try {
      const response = await getAllRIO();
      console.log("response", response);
      if (response.status == 200 && response && response.data) {
        const mappedRIO = response.data.map((item: RIOType) => ({
          id: item.rioId,
          ...item,
        }));
        setRIOS(mappedRIO);
      }
    } catch (error) {
      console.error("Error fetching RIO:", error);
      showAlert("An error occurred while fetching RIO.", "error");
    }
  };

  const fetchAssemblyConstituencies = async () => {
    try {
      const response = await getAllAssemblyConstituency();
      if (response && response.data) {
        const mappedAssemblyConstituency = response.data.map(
          (item: AssemblyConstituencyType) => ({
            id: item.assemblyConstituencyId,
            ...item,
          })
        );
        setAssemblyConstituency(mappedAssemblyConstituency);
      }
    } catch (error) {
      console.error("Error fetching Assembly Constituency:", error);
      showAlert(
        "An error occurred while fetching Assembly Constituency.",
        "error"
      );
    }
  };

  const fetchUserData = async () => {
    if (mode === "edit" && userId) {
      try {
        setLoading(true);
        const response = await client.get(`${baseUrl}/users/${userId}`);
        if (response.status === 200) {
          const userData = response.data;
          const roleValue =
            userData.roleId != null ? userData.roleId : userData.role;
          const newFormData = {
            userId: userData.userId,
            fullName: userData.fullName || "",
            mobileNo: userData.mobileNo || "",
            email: userData.email || "",
            rank: userData.rankId || null,
            policeStation: userData.policeStation || "",
            assemblyConstituencyId: userData.assemblyConstituencyId || null,
            zoneId: userData.zoneId || null,
            location: userData.location || "",
            dateOfBirth: userData.dateOfBirth
              ? new Date(userData.dateOfBirth).toISOString().split("T")[0]
              : "",
            role: roleValue != null ? Number(roleValue) : null,
            rioId: userData.rioId || null,
            employeeId: userData.employeeId || null,
            generalNumber: userData.generalNumber || null,
          };
          setFormData(newFormData);
        } else {
          showAlert("Failed to fetch user data.", "error");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        showAlert("Error fetching user data.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        await Promise.all([
          fetchAllRanks(),
          fetchRoles(),
          fetchRIOs(),
          fetchZones(),
          fetchAssemblyConstituencies(),
          mode === "edit" ? fetchUserData() : Promise.resolve(),
        ]);
      } catch {
        showAlert("Error loading data.", "error");
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, [mode, userId]);

  useEffect(() => {
    if (allZones.length > 0 && formData.rioId) {
      filterZonesByRIO(formData.rioId);
    }
    if (assemblyConstituency.length > 0 && formData.zoneId) {
      filterAssemblyByZone(formData.zoneId);
    }
  }, [allZones, assemblyConstituency, formData.zoneId, formData.rioId]);

  useEffect(() => {
    if (mode === "edit" && formData.role != null && roles.length > 0) {
      const roleExists = roles.some((role) => role.roleId === formData.role);
      if (!roleExists) {
        setFormData((prev) => ({ ...prev, role: null }));
      }
    }
  }, [roles, formData.role, mode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    if (name) {
      let newValue: number | null | string = value;

      if (name === "rank") {
        newValue = value === 0 ? null : Number(value);
      } else if (name === "role") {
        newValue = value === "" ? null : Number(value);
      } else if (name === "assemblyConstituencyId") {
        newValue = value === "" ? null : Number(value);
      } else if (name === "zoneId") {
        newValue = value === "" ? null : Number(value);
        setFormData((prev) => ({ ...prev, assemblyConstituencyId: null }));
      } else if (name === "rioId") {
        newValue = value === "" ? null : Number(value);
        setFormData((prev) => ({ ...prev, zoneId: null }));
      }

      setFormData((prev) => ({ ...prev, [name]: newValue }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const filteredValue = rawValue.replace(/[^0-9-]/g, "");
    const name = e.target.name;
    setFormData((prev) => ({ ...prev, [name]: filteredValue }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const validateForm = () => {
    const requiredFields = [
      "fullName",
      "mobileNo",
      "dateOfBirth",
      "role",
      "employeeId",
    ];
    const newErrors: { [key: string]: boolean } = {};
    requiredFields.forEach((field) => {
      if (!String(formData[field as keyof FormData]).trim()) {
        newErrors[field] = true;
      }
    });

    if (formData.mobileNo.length !== 10) {
      newErrors.mobileNo = true;
    }
    if (formData.employeeId === null) {
      newErrors.employeeId = true;
    }
    if (formData.role === null) {
      newErrors.role = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      showAlert("Please fill in all required fields correctly.", "error");
      return;
    }

    try {
      setLoading(true);
      const submissionData = {
        userId: formData.userId,
        fullName: formData.fullName,
        mobileNo: formData.mobileNo,
        email: formData.email,
        policeStation: formData.policeStation,
        assemblyConstituencyId: formData.assemblyConstituencyId || null,
        zoneId: formData.zoneId || null,
        location: formData.location,
        dateOfBirth: formData.dateOfBirth
          ? new Date(formData.dateOfBirth).toISOString()
          : "",
        rankId: formData.rank || null,
        roleId: formData.role,
        rioId: formData.rioId,
        generalNumber: formData.generalNumber,
        employeeId: formData.employeeId,
      };

      const url =
        mode === "create"
          ? `${baseUrl}${REGISTER}`
          : `${baseUrl}/users/${userId}`;
      const method = mode === "create" ? client.post : client.put;
      const response = await method(url, submissionData);
      if (response?.status === 200 || response?.status === 201) {
        showAlert(
          mode === "create"
            ? "User Created Successfully!"
            : "User Updated Successfully!",
          "success"
        );
        closeUserDialog(mode);
      } else {
        showAlert("Failed to save user.", "error");
      }
    } catch (error: any) {
      console.error(
        `Error ${mode === "create" ? "creating" : "updating"} user:`,
        error
      );
      showAlert(error, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      {loading || dataLoading ? (
        <Box sx={containerSx}>
          <CircularProgress />
          <Typography variant="h6">Loading...</Typography>
        </Box>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            {[
              { label: "Name", name: "fullName" },
              { label: "Email", name: "email" },
              { label: "EmployeeId", name: "employeeId" },
              { label: "Office/Station", name: "policeStation" },
              { label: "Location", name: "location" },
              { label: "General Number", name: "generalNumber" },
            ].map(({ label, name }) => {
              const isRequired = ["fullName", "employeeId"].includes(name);
              return (
                <Grid key={name} item xs={12} sm={6}>
                  <Typography variant="subtitle1" fontSize={14} color="black">
                    {label}
                    {isRequired && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <TextField
                    fullWidth
                    name={name}
                    value={formData[name as keyof FormData] || ""}
                    onChange={handleInputChange}
                    error={!!errors[name]}
                    helperText={errors[name] ? "This field is required" : ""}
                    variant="outlined"
                    slotProps={{
                      input: {
                        sx: labelStyles,
                      },
                    }}
                  />
                </Grid>
              );
            })}

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Mobile No<span style={{ color: "red" }}> *</span>
              </Typography>
              <TextField
                fullWidth
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleChange}
                variant="outlined"
                error={!!errors.mobileNo}
                helperText={
                  errors.mobileNo
                    ? formData.mobileNo
                      ? "Mobile number must be exactly 10 digits"
                      : "This field is required"
                    : ""
                }
                inputProps={{
                  maxLength: 10,
                }}
                slotProps={{
                  input: {
                    sx: {
                      padding: "6px",
                      borderRadius: "8px",
                      height: "48px",
                      fontSize: "12px",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                RIO
              </Typography>
              <Select
                name="rioId"
                value={formData.rioId ?? ""}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  fontSize: "12px",
                }}
              >
                <MenuItem value="" disabled>
                  Select RIO
                </MenuItem>
                {rios.map((rio) => (
                  <MenuItem key={rio.rioId} value={rio.rioId}>
                    {rio.rioName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Zone
              </Typography>
              <Select
                name="zoneId"
                value={formData.zoneId ?? ""}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
                disabled={!formData.rioId || filteredZones.length === 0}
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  fontSize: "12px",
                }}
              >
                <MenuItem value="" disabled>
                  {formData.assemblyConstituencyId && filteredZones.length === 0
                    ? "No zones available for selected assembly"
                    : "Select Zone"}
                </MenuItem>
                {filteredZones.map((zone) => (
                  <MenuItem key={zone.zoneId} value={zone.zoneId}>
                    {zone.zoneName}
                  </MenuItem>
                ))}
              </Select>
              {formData.assemblyConstituencyId &&
                filteredZones.length === 0 && (
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "12px", mt: 0.5 }}
                  >
                    No zones found for the selected Assembly Constituency
                  </Typography>
                )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Assembly Constituency
              </Typography>
              <Select
                name="assemblyConstituencyId"
                value={formData.assemblyConstituencyId ?? ""}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  fontSize: "12px",
                }}
                disabled={
                  !formData.zoneId || filteredAssemblyConstituency.length === 0
                }
              >
                <MenuItem value="" disabled>
                  Select Assembly Constituency
                </MenuItem>
                {assemblyConstituency.map((assembly) => (
                  <MenuItem
                    key={assembly.assemblyConstituencyId}
                    value={assembly.assemblyConstituencyId}
                  >
                    {assembly.assemblyConstituencyName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Rank
              </Typography>
              <Select
                name="rank"
                value={formData.rank ?? ""}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  fontSize: "12px",
                }}
              >
                <MenuItem value="" disabled>
                  Select Rank
                </MenuItem>
                <MenuItem value={0}>No Rank</MenuItem>
                {ranks.map((rank) => (
                  <MenuItem key={rank.id} value={rank.id}>
                    {rank.rankName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Role<span style={{ color: "red" }}> *</span>
              </Typography>
              <Select
                name="role"
                value={formData.role ?? ""}
                onChange={handleSelectChange}
                fullWidth
                error={!!errors.role}
                displayEmpty
                sx={{
                  borderRadius: "8px",
                  height: "48px",
                  fontSize: "12px",
                }}
              >
                <MenuItem value="" disabled>
                  Select Role
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.roleId} value={role.roleId}>
                    {role.roleName}
                  </MenuItem>
                ))}
              </Select>
              {errors.role && (
                <Typography
                  color="error"
                  sx={{ fontSize: "12px", marginLeft: 2 }}
                >
                  Please select a role
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" fontSize={14} color="black">
                Date of Birth<span style={{ color: "red" }}> *</span>
              </Typography>
              <TextField
                fullWidth
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                variant="outlined"
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth ? "This field is required" : ""}
                slotProps={{
                  htmlInput: { max: dayjs().format("YYYY-MM-DD") },
                  input: {
                    sx: {
                      borderRadius: "8px",
                      fontSize: "12px",
                      height: "48px",
                    },
                  },
                }}
                sx={{
                  borderRadius: "8px",
                  "& input::-webkit-calendar-picker-indicator": {
                    cursor: "pointer",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container justifyContent="flex-end" mt={3}>
            <Button
              variant="outlined"
              onClick={() => closeUserDialog("Cancel")}
              sx={cancelButtonStyles}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || dataLoading}
              sx={{
                ml: 2,
                borderRadius: "8px",
                textTransform: "inherit",
                backgroundColor: colors.indigoBlueColor,
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : null}
              {mode === "create" ? "Create User" : "Update User"}
            </Button>
          </Grid>
        </form>
      )}
    </Container>
  );
};

export default UserForm;

const cancelButtonStyles: SxProps = {
  borderRadius: "8px",
  textTransform: "inherit",
  color: "#ABABAB",
  border: "1px solid #ABABAB",
};

export const labelStyles: SxProps = {
  fontSize: "12px",
  padding: "6px",
  borderRadius: "8px",
  height: "48px",
  "&:-webkit-autofill": {
    boxShadow: "0 0 0 1000px white inset",
    WebkitTextFillColor: "black",
    borderRadius: "8px",
  },
};

const containerSx: SxProps = {
  height: "70vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
};
